/** Persisted exec pricing session (CONF-106 part 3, spec §1/§2.1).
 *  The record is the ONLY home of cost data (µ$ baselines, integration %):
 *  execAccess-gated, create-only, immutable — one record per saved quoteId.
 *  basisSnapshot freezes the exact engine inputs used at save so hydration
 *  never re-derives against a moved catalog. Writes are FAIL-CLOSED. */
import { generateClient } from 'aws-amplify/api';
import { createExecQuotePricing, getExecQuotePricing } from '../graphql/execQuotePricingOps';
import { snapshotToPlain, snapshotFromPlain } from '../pricing/snapshotSerde';

export function recordOverridesShape(overrides, snapshot) {
  const componentChanges = [];
  for (const [productSku, comps] of Object.entries(overrides.componentCosts)) {
    for (const [componentSku, overrideMicros] of Object.entries(comps)) {
      componentChanges.push({
        productSku, componentSku,
        catalogMicros: snapshot?.componentCostMicros?.[componentSku] ?? null,
        overrideMicros,
      });
    }
  }
  return {
    componentChanges,
    integrationPct: overrides.integrationBp !== null
      ? { catalogBp: snapshot?.integrationBp ?? null, overrideBp: overrides.integrationBp }
      : null,
  };
}

export function overridesFromRecordShape(shape) {
  const componentCosts = {};
  for (const c of shape?.componentChanges ?? []) {
    (componentCosts[c.productSku] ??= {})[c.componentSku] = c.overrideMicros;
  }
  return { componentCosts, integrationBp: shape?.integrationPct?.overrideBp ?? null };
}

export function buildExecQuotePricingRecord({ quoteId, execPricing, actor, appVersion, savedAt }) {
  const { snapshot, overrides, overrideSetId } = execPricing;
  return {
    id: quoteId,
    quoteId,
    overrideSetId,
    vendor: snapshot.vendor,
    quarter: snapshot.quarter,
    basisSnapshot: JSON.stringify(snapshotToPlain(snapshot)),
    overrides: JSON.stringify(recordOverridesShape(overrides, snapshot)),
    actorEmail: actor?.email ?? null,
    actorSub: actor?.sub ?? null,
    appVersion: appVersion ?? null,
    createdAt: savedAt,
  };
}

export async function writeExecQuotePricing(input) {
  try {
    const client = generateClient();
    const res = await client.graphql({ query: createExecQuotePricing, variables: { input } });
    return !!res?.data?.createExecQuotePricing;
  } catch (error) {
    console.error('ExecQuotePricing write FAILED:', error);
    return false;
  }
}

export async function fetchExecQuotePricing(quoteId) {
  try {
    const client = generateClient();
    const res = await client.graphql({ query: getExecQuotePricing, variables: { id: quoteId } });
    const row = res?.data?.getExecQuotePricing;
    if (!row) return null;
    return {
      ...row,
      basisSnapshot: snapshotFromPlain(JSON.parse(row.basisSnapshot)),
      overrides: overridesFromRecordShape(JSON.parse(row.overrides)),
    };
  } catch (error) {
    console.error('ExecQuotePricing fetch failed (session unavailable):', error);
    return null;
  }
}
